import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { authRedirectUrl } from '../authUtils.js';
import { supabase } from '../supabase.js';

const DEMO_MODE = import.meta.env.VITE_DEMO_MODE === 'true';
const DEMO_EMAIL = import.meta.env.VITE_DEMO_EMAIL ?? 'demo@onit.am';
const DEMO_PASSWORD = import.meta.env.VITE_DEMO_PASSWORD ?? 'DemoOnit2026!';

/** Email/password sign-in. Sign-up is enabled in config for first-run setup. */
export function SignIn() {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [mode, setMode] = useState<'signIn' | 'signUp' | 'forgotPassword'>('signIn');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!supabase) return;
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      if (mode === 'signIn') {
        const { error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) throw error;
      } else if (mode === 'signUp') {
        const { error } = await supabase.auth.signUp({ email, password });
        if (error) throw error;
        setNotice(t('auth.accountCreated'));
        setMode('signIn');
      } else {
        const { error } = await supabase.auth.resetPasswordForEmail(email, {
          redirectTo: authRedirectUrl(),
        });
        if (error) throw error;
        setNotice(t('auth.resetLinkSent'));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  async function tryDemo() {
    if (!supabase) return;
    setBusy(true);
    setError(null);
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email: DEMO_EMAIL,
        password: DEMO_PASSWORD,
      });
      if (error) throw error;
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="auth-screen">
      <header className="landing-nav">
        <a href="/" className="landing-logo">
          Orio
        </a>
      </header>
      <form className="auth-card" onSubmit={submit}>
        <h2>
          {mode === 'signIn'
            ? t('common.signIn')
            : mode === 'signUp'
              ? t('auth.createAccount')
              : t('auth.forgotPasswordTitle')}
        </h2>
        {mode === 'forgotPassword' && <p className="muted">{t('auth.forgotPasswordBody')}</p>}
        <label>
          {t('auth.email')}
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            autoComplete="email"
          />
        </label>
        {mode !== 'forgotPassword' && (
          <label>
            {t('auth.password')}
            <div className="auth-password">
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={6}
                autoComplete={mode === 'signIn' ? 'current-password' : 'new-password'}
              />
              <button
                type="button"
                className="auth-toggle"
                onClick={() => setShowPassword((v) => !v)}
                tabIndex={-1}
                aria-label={showPassword ? t('common.hidePassword') : t('common.showPassword')}
              >
                {showPassword ? t('common.hide') : t('common.show')}
              </button>
            </div>
          </label>
        )}
        {error && <p className="auth-error">{error}</p>}
        {notice && <p className="auth-notice">{notice}</p>}
        <button type="submit" disabled={busy}>
          {busy
            ? '…'
            : mode === 'signIn'
              ? t('common.signIn')
              : mode === 'signUp'
                ? t('auth.createAccount')
                : t('auth.sendResetLink')}
        </button>
        {DEMO_MODE && mode === 'signIn' && (
          <>
            <p className="muted" style={{ fontSize: 12, textAlign: 'center', margin: '12px 0 0' }}>
              {t('demo.or')}
            </p>
            <button type="button" className="btn" disabled={busy} onClick={() => void tryDemo()}>
              {t('demo.tryDemo')}
            </button>
            <p className="muted" style={{ fontSize: 12, marginTop: 8, textAlign: 'center' }}>
              {t('demo.signInHint')}
            </p>
          </>
        )}
        {mode === 'signIn' && (
          <button
            type="button"
            className="link"
            onClick={() => {
              setMode('forgotPassword');
              setError(null);
              setNotice(null);
            }}
          >
            {t('auth.forgotPassword')}
          </button>
        )}
        {mode !== 'forgotPassword' && !DEMO_MODE ? (
          <button
            type="button"
            className="link"
            onClick={() => {
              setMode(mode === 'signIn' ? 'signUp' : 'signIn');
              setError(null);
              setNotice(null);
            }}
          >
            {mode === 'signIn' ? t('auth.needAccount') : t('auth.haveAccount')}
          </button>
        ) : (
          <button
            type="button"
            className="link"
            onClick={() => {
              setMode('signIn');
              setError(null);
              setNotice(null);
            }}
          >
            {t('auth.backToSignIn')}
          </button>
        )}
      </form>
    </div>
  );
}
